/**
 * This component will be used as a base component to other components like Text Input, Email Input, Password Input, etc.
 */
import { isRTL } from "@core/shared/utils";
import { Input } from "@design-system/ui/input";
import { useFormControl, type FormControlProps } from "@mongez/react-form";
import { type FunctionComponent, type ReactNode } from "react";
import { InputError } from "./InputError";
import { InputLabel } from "./InputLabel";
import { InputDescription, InputHint } from "./Utils";

export type BaseInputProps = FormControlProps & {
  Component?: FunctionComponent;
  hint?: ReactNode;
  description?: ReactNode;
  isCollectable?: boolean;
  autoFocus?: boolean;
  /**
   * Content to display on the right side of the input
   */
  rightSide?: ReactNode;
  /**
   * Content to display on the left side of the input
   */
  leftSide?: ReactNode;
  /**
   * Whether to keep the error visible even if the input is not focused
   */
  keepErrorVisible?: boolean;
};

export function BaseInput({
  Component = Input,
  label,
  description,
  hint,
  isCollectable = true,
  rightSide,
  leftSide,
  keepErrorVisible = true,
  ...props
}: BaseInputProps) {
  const {
    changeValue,
    value,
    error,
    type,
    id,
    disabled,
    inputRef,
    otherProps,
  } = useFormControl(props, { isCollectable: () => isCollectable });

  const isRTLValue = isRTL();

  return (
    <div className="w-full flex flex-col gap-1.5">
      <div className="flex flex-col gap-1">
        {label && (
          <InputLabel htmlFor={id} required={props.required}>
            {label}
          </InputLabel>
        )}
        {description && <InputDescription>{description}</InputDescription>}
      </div>
      <div className="relative">
        {leftSide && (
          <div
            className={`absolute ${isRTLValue ? "right-0 pr-3" : "left-0 pl-3"} top-0 h-full flex items-center`}>
            {leftSide}
          </div>
        )}
        <Component
          value={value}
          onChange={e => changeValue(e.target.value)}
          id={id}
          type={type}
          ref={inputRef}
          disabled={disabled}
          className={`${leftSide ? (isRTLValue ? "pr-10" : "pl-10") : ""} ${rightSide ? (isRTLValue ? "pl-10" : "pr-10") : ""}`}
          {...otherProps}
        />
        {rightSide && (
          <div
            className={`absolute ${isRTLValue ? "left-0 pl-3" : "right-0 pr-3"} top-0 h-full flex items-center`}>
            {rightSide}
          </div>
        )}
      </div>
      <div className="flex flex-col gap-1">
        {hint && !error && <InputHint>{hint}</InputHint>}
        <InputError error={error} keepErrorVisible={keepErrorVisible} />
      </div>
    </div>
  );
}
